import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api, { apiError, getToken, API } from "@/lib/api";
import { CustomerShell } from "@/components/CustomerShell";
import { StatusBadge } from "@/components/StatusBadge";
import { SafetyReport } from "@/components/SafetyReport";
import { BeforeAfterCompare } from "@/components/BeforeAfterCompare";
import { ReviseEstimate, CustomerCancel } from "@/components/CustomerBookingActions";
import { BookingChat } from "@/components/BookingChat";
import { CouponBox } from "@/components/CouponBox";
import { MaterialChoice, CustomerMaterials, BillView } from "@/components/MaterialsPanel";
import { rupee, whatsappLink } from "@/lib/constants";
import { toast } from "sonner";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { Timeline as ActivityLog } from "@/components/Timeline";
import {
    ChevronLeft, Loader2, Star, Download, Share2, ShieldCheck, Check, X,
    MessageCircle, UserCheck, Phone, HeartHandshake, AlertCircle, Camera, QrCode, Smartphone,
} from "lucide-react";

const DONE = ["COMPLETED", "PAYMENT PENDING", "PAID", "CLOSED"];

import { loadRazorpay, openRazorpay } from "@/lib/razorpay";

export default function BookingDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [b, setB] = useState(null);
    const [busy, setBusy] = useState(false);
    const [stars, setStars] = useState(0);
    const [showPref, setShowPref] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [reportText, setReportText] = useState("");
    const [payCfg, setPayCfg] = useState(null);
    const [showPay, setShowPay] = useState(false);
    const [payMode, setPayMode] = useState("service");
    const [reworkNote, setReworkNote] = useState("");
    const [upiRef, setUpiRef] = useState("");
    const [matSrc, setMatSrc] = useState(null);

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/bookings/${id}`); setB(data); }
        catch { toast.error("Could not load booking"); }
    }, [id]);

    useEffect(() => { load(); }, [load]);
    useLiveRefresh(load, 15000);
    useEffect(() => { api.get("/payment-config").then((r) => setPayCfg(r.data)).catch(() => {}); }, []);

    if (!b) return <CustomerShell noNav><div className="flex min-h-screen items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div></CustomerShell>;

    const est = b.estimate;
    const decide = async (decision) => {
        setBusy(true);
        try {
            await api.post(`/bookings/${id}/estimate/${decision}`, decision === "approve" && est.materials > 0 ? { material_source: matSrc } : {});
            toast.success(decision === "approve" ? "Estimate approved — work can begin" : "Estimate rejected");
            await load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const submitRating = async () => {
        if (!stars) return toast.error("Please select a rating");
        setBusy(true);
        try {
            await api.post(`/bookings/${id}/rating`, { stars });
            toast.success("Thanks for your rating!");
            await load();
            if (b.professional_id) setShowPref(true);
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const savePref = async (yes) => {
        setShowPref(false);
        if (!yes) return;
        try { await api.post("/preferred-pros", { professional_id: b.professional_id }); toast.success(`${b.professional?.name} saved to My HOMIIE Team!`); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    const submitReport = async () => {
        if (!reportText.trim()) return toast.error("Please describe the problem");
        setBusy(true);
        try {
            await api.post("/complaints", { booking_id: id, description: reportText, photos: [] });
            toast.success("Report submitted. HOMIIE support will review it.");
            setShowReport(false); setReportText("");
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const downloadInvoice = () => {
        window.open(`${API}/bookings/${id}/invoice.pdf?auth=${getToken()}`, "_blank");
    };

    const isPaid = b.payment && (b.payment.status === "Paid" || b.payment.status === "CASH_CONFIRMED");
    const payPending = b.payment && ["PendingConfirmation", "CASH_PENDING_CONFIRMATION"].includes(b.payment.status);
    const canPay = est && est.status === "APPROVED" && ["WORK DONE - PAYMENT PENDING", "PAYMENT PENDING"].includes(b.status) && est.amount_due > 0 && !isPaid && !payPending;

    const vf = b.visit_fee_payment;
    const vfPaid = vf && (vf.status === "Paid" || vf.status === "CASH_CONFIRMED");
    const vfPending = vf && ["PendingConfirmation", "CASH_PENDING_CONFIRMATION"].includes(vf.status);
    const needVisitFee = b.status === "VISIT FEE PENDING" && !vfPaid && !vfPending;
    const inReview = b.status === "CUSTOMER REVIEW";
    const inRework = b.status === "REVISION REQUIRED";

    const amountDue = est ? (est.amount_due ?? est.total ?? 0) : 0;
    const payAmount = payMode === "visit" ? (b.visit_fee || 0) : amountDue;
    const upiUri = payCfg?.upi_vpa && payAmount
        ? `upi://pay?pa=${encodeURIComponent(payCfg.upi_vpa)}&pn=${encodeURIComponent(payCfg.upi_payee_name || "HOMIIE")}&am=${payAmount}&cu=INR&tn=${encodeURIComponent(b.booking_id)}`
        : "";

    const openPay = (mode) => { setPayMode(mode); setShowPay(true); };

    const payFinalRazorpay = async () => {
        setBusy(true);
        try {
            const { data: order } = await api.post(`/bookings/${id}/pay-final/razorpay/order`);
            const ok = await loadRazorpay();
            if (!ok) { toast.error("Could not load payment gateway. Check your connection."); setBusy(false); return; }
            openRazorpay(order, {
                onSuccess: async (res) => {
                    try {
                        const { data } = await api.post(`/bookings/${id}/pay-final/razorpay/verify`, {
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        toast.success(`Payment successful — invoice ${data.invoice_number} ready`);
                        await load();
                    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Payment verification failed"); }
                    finally { setBusy(false); }
                },
                onDismiss: () => { setBusy(false); toast("Payment cancelled — you can pay anytime."); },
                onError: () => { setBusy(false); toast.error("Payment failed. Please try again."); },
            });
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
    };

    const payUpi = async (method) => {
        setBusy(true);
        try {
            const endpoint = payMode === "visit" ? "pay-visit-fee" : "pay-upi";
            await api.post(`/bookings/${id}/${endpoint}`, { reference: upiRef.trim() || null, method });
            toast.success(payMode === "visit" ? "Visit fee submitted — HOMIIE will confirm it shortly" : "Payment submitted — HOMIIE will confirm it shortly");
            setShowPay(false); setUpiRef("");
            await load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const reviewWork = async (decision) => {
        if (decision === "reject" && !reworkNote.trim()) return toast.error("Please tell the professional what to redo");
        setBusy(true);
        try {
            await api.post(`/bookings/${id}/review/${decision}`, { note: reworkNote.trim() || null });
            toast.success(decision === "approve" ? "Work approved — you can pay now" : "Rework requested");
            setReworkNote("");
            await load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <CustomerShell noNav>
            <div className="sticky top-0 z-20 flex items-center gap-3 bg-navy px-5 pb-4 pt-5 text-white">
                <button data-testid="detail-back-btn" onClick={() => nav(-1)} className="rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20"><ChevronLeft size={20} /></button>
                <div>
                    <p className="text-xs text-white/50">Booking</p>
                    <h1 className="font-heading text-lg font-bold">{b.booking_id}</h1>
                </div>
                <div className="ml-auto"><StatusBadge status={b.status} /></div>
            </div>

            <div className="space-y-4 px-5 py-5">
                {/* summary */}
                <Card>
                    <h3 className="font-heading font-bold text-navy">{b.service_name}</h3>
                    <p className="text-sm text-navy/60">{b.problem}</p>
                    {b.description && <p className="mt-1 text-sm text-navy/50">{b.description}</p>}
                    <div className="mt-3 space-y-1.5 border-t border-navy/10 pt-3 text-sm">
                        <Row label="Address" value={b.address_line} />
                        <Row label="Area" value={b.area_name} />
                        <Row label="When" value={`${b.preferred_date} · ${b.preferred_window}`} />
                    </div>
                </Card>

                {/* visit fee */}
                {needVisitFee && (
                    <Card>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-heading font-bold text-navy">Pay visit fee to confirm</h3>
                                <p className="text-sm text-navy/55">A verified professional is assigned only after your visit fee is paid.</p>
                            </div>
                            <span className="font-heading text-lg font-extrabold text-navy">{rupee(b.visit_fee)}</span>
                            <span className="block text-[10px] text-navy/45" data-testid="visit-fee-gst-note">incl. GST — adjusted in final bill</span>
                        </div>
                        <button data-testid="pay-visit-fee-btn" disabled={!payCfg?.upi_vpa} onClick={() => openPay("visit")} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-orange py-3 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-50"><QrCode size={17} /> PAY ₹{b.visit_fee} VISIT FEE</button>
                    </Card>
                )}
                {vfPending && b.status === "VISIT FEE PAYMENT PENDING" && (
                    <Card>
                        <div className="flex items-center gap-3" data-testid="visit-fee-pending-banner">
                            <div className="rounded-xl bg-amber-50 p-2.5"><Loader2 size={18} className="animate-spin text-amber-600" /></div>
                            <div className="flex-1">
                                <h3 className="font-heading text-sm font-bold text-navy">Visit fee confirmation pending</h3>
                                <p className="text-xs text-navy/55">Your {rupee(b.visit_fee)} visit fee is awaiting HOMIIE confirmation. A professional is assigned right after.</p>
                            </div>
                        </div>
                    </Card>
                )}

                {/* customer review */}
                {est?.material_source && ["WORK IN PROGRESS", "CUSTOMER REVIEW", "REVISION REQUIRED"].includes(b.status) && <CustomerMaterials b={b} onChange={load} />}
                {inReview && (
                    <Card>
                        <h3 className="font-heading font-bold text-navy">Your service is ready for review</h3>
                        <p className="text-sm text-navy/55">Review the completed work, then approve to proceed to payment or request a rework.</p>
                        {(b.evidence?.length > 0) ? (
                            <div data-testid="review-evidence"><BeforeAfterCompare evidence={b.evidence} /></div>
                        ) : b.photos?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid="review-photos">
                                {b.photos.map((p) => <img key={p} src={`${API}/files/${p}?auth=${getToken()}`} alt="Work" className="h-20 w-20 rounded-lg border border-navy/10 object-cover" />)}
                            </div>
                        )}
                        <textarea data-testid="rework-note-input" value={reworkNote} onChange={(e) => setReworkNote(e.target.value)} rows={2} placeholder="Add a note if requesting a rework…" className="mt-3 w-full rounded-xl border border-navy/15 bg-white px-4 py-2.5 text-sm text-navy outline-none focus:border-orange" />
                        <div className="mt-3 flex gap-2">
                            <button data-testid="approve-work-btn" disabled={busy || est?.material_revision?.status === "PENDING"} onClick={() => reviewWork("approve")} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-emerald-500 py-3 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-60"><Check size={17} /> APPROVE & PAY</button>
                            <button data-testid="request-rework-btn" disabled={busy} onClick={() => reviewWork("reject")} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-red-300 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-50"><X size={17} /> REQUEST REWORK</button>
                        </div>
                    </Card>
                )}
                {b.evidence?.length > 0 && ["WORK DONE - PAYMENT PENDING", "PAYMENT PENDING", "PAID", "COMPLETED", "CLOSED"].includes(b.status) && (
                    <Card>
                        <h3 className="font-heading font-bold text-navy" data-testid="work-photos-title">Work photos</h3>
                        <p className="text-xs text-navy/50">Before and after photos of the work done.</p>
                        <BeforeAfterCompare evidence={b.evidence} />
                        {est?.material_bill && <div className="mt-3"><p className="text-xs font-bold text-navy/60">Material bill</p><BillView bill={est.material_bill} /></div>}
                    </Card>
                )}
                {inRework && (
                    <Card>
                        <div className="flex items-center gap-3" data-testid="rework-banner">
                            <div className="rounded-xl bg-amber-50 p-2.5"><AlertCircle size={18} className="text-amber-600" /></div>
                            <div className="flex-1">
                                <h3 className="font-heading text-sm font-bold text-navy">Rework in progress</h3>
                                <p className="text-xs text-navy/55">Your professional is redoing the work and will re-submit it for your review.</p>
                            </div>
                        </div>
                    </Card>
                )}

                {/* professional */}
                {b.professional && (
                    <Card>
                        <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-lg font-bold text-orange">{b.professional.name[0]}</div>
                            <div className="flex-1">
                                <div className="flex items-center gap-1.5">
                                    <h3 className="font-heading font-bold text-navy">{b.professional.name}</h3>
                                    {b.professional.verification_status === "Verified" && <UserCheck size={15} className="text-emerald-500" />}
                                </div>
                                <p className="text-xs text-navy/50">{b.professional.pro_id} · ⭐ {b.professional.rating || "New"}</p>
                            </div>
                            <a href={`tel:${b.professional.phone}`} data-testid="call-pro-btn" className="rounded-full bg-emerald-50 p-2.5 text-emerald-600"><Phone size={18} /></a>
                        </div>
                    </Card>
                )}

                {/* estimate */}
                {est && (
                    <Card>
                        <div className="mb-2 flex items-center justify-between">
                            <h3 className="font-heading font-bold text-navy">Estimate</h3>
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${est.status === "APPROVED" ? "bg-emerald-50 text-emerald-700" : est.status === "REJECTED" ? "bg-red-50 text-red-700" : "bg-orange/15 text-orange-dark"}`}>{est.status}</span>
                        </div>
                        <div className="space-y-1.5 text-sm">
                            <Row label="Visit charge" value={rupee(est.visit_charge)} />
                            <Row label="Labour" value={rupee(est.labour)} />
                            <Row label="Materials" value={est.material_source === "customer" ? "Supplied by you" : rupee(est.materials)} />
                            {est.other > 0 && <Row label="Convenience Fee" value={rupee(est.other)} />}
                            {est.platform_fee > 0 && <Row label="Platform fee" value={rupee(est.platform_fee)} />}
                            {est.discount > 0 && <Row label="Discount" value={`- ${rupee(est.discount)}`} />}
                            {est.coupon_discount > 0 && <Row label={`Coupon ${est.coupon_code}`} value={`- ${rupee(est.coupon_discount)}`} />}
                            <div className="flex justify-between border-t border-navy/10 pt-2 font-heading text-base font-extrabold text-navy">
                                <span>Total</span><span>{rupee(est.total)}</span>
                            </div>
                            {est.previous_total != null && <p data-testid="estimate-revised-note" className="text-xs font-semibold text-teal">Revised estimate — earlier {rupee(est.previous_total)}</p>}
                            {est.material_cash_received > 0 && <Row label="Less: materials paid in cash to Pro" value={`- ${rupee(est.material_cash_received)}`} />}
                            {est.material_payment?.status === "Paid" && <Row label="Less: materials paid in advance" value={`- ${rupee(est.material_payment.amount)}`} />}
                            {est.amount_due < est.total && (
                                <>
                                    {est.visit_fee_paid > 0 && <Row label="Less: visit fee already paid" value={`- ${rupee(est.visit_fee_paid)}`} />}
                                    {est.part_paid > 0 && <Row label="Less: part payment received" value={`- ${rupee(est.part_paid)}`} />}
                                    <div className="flex justify-between font-heading font-extrabold text-emerald-700" data-testid="estimate-amount-due"><span>Balance to pay</span><span>{rupee(est.amount_due)}</span></div>
                                </>
                            )}
                            <div className="text-[11px] text-navy/45">All amounts include GST.</div>
                        </div>
                        {est.status === "APPROVED" && !isPaid && !payPending && !(b.payment?.received > 0) && !["PAID", "COMPLETED", "CLOSED", "CANCELLED"].includes(b.status) && <CouponBox b={b} onChange={load} />}
                        {est.status === "PENDING" && (
                            <div className="mt-4 space-y-2">
                                {est.materials > 0 && <MaterialChoice amount={est.materials} value={matSrc} onChange={setMatSrc} />}
                                <div className="flex gap-2">
                                    <button data-testid="approve-estimate-btn" disabled={busy || (est.materials > 0 && !matSrc)} onClick={() => decide("approve")} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-emerald-500 py-3 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-60"><Check size={17} /> APPROVE</button>
                                    <button data-testid="reject-estimate-btn" disabled={busy} onClick={() => decide("reject")} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-red-300 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-50"><X size={17} /> REJECT</button>
                                </div>
                                <ReviseEstimate id={id} onDone={load} />
                                <a href={whatsappLink(`Hi HOMIIE, I have a question about estimate for ${b.booking_id}`)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-full border border-navy/15 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-orange"><MessageCircle size={16} /> ASK HOMIIE</a>
                            </div>
                        )}
                        {est.status === "REVISION_REQUESTED" && <p data-testid="estimate-revision-waiting" className="mt-3 rounded-xl bg-amber-50 p-3 text-xs font-semibold text-amber-800">You asked for a revised estimate: “{est.revision_note}”. Your professional will send corrected charges shortly.</p>}
                    </Card>
                )}

                {/* pay via UPI */}
                {canPay && (
                    <Card>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-heading font-bold text-navy">Pay & get your invoice</h3>
                                <p className="text-sm text-navy/55">Pay securely via UPI, cards or netbanking — invoice generated instantly.</p>
                            </div>
                            <span className="font-heading text-lg font-extrabold text-navy">{rupee(amountDue)}</span>
                        </div>
                        <button data-testid="pay-final-btn" disabled={busy} onClick={payFinalRazorpay} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-orange py-3 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-50">{busy ? <Loader2 size={17} className="animate-spin" /> : <QrCode size={17} />} PAY {rupee(amountDue)} SECURELY</button>
                    </Card>
                )}

                {payPending && (
                    <Card>
                        <div className="flex items-center gap-3" data-testid="payment-pending-banner">
                            <div className="rounded-xl bg-amber-50 p-2.5"><Loader2 size={18} className="animate-spin text-amber-600" /></div>
                            <div className="flex-1">
                                <h3 className="font-heading text-sm font-bold text-navy">Payment confirmation pending</h3>
                                <p className="text-xs text-navy/55">Your {b.payment.method === "CASH" ? "cash" : "UPI"} payment of {rupee(b.payment.amount)} is awaiting HOMIIE confirmation. Your invoice is generated once confirmed.</p>
                            </div>
                        </div>
                    </Card>
                )}

                {/* payment / invoice */}
                {b.payment && (
                    <Card>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-heading font-bold text-navy">Payment</h3>
                                <p className="text-sm text-navy/55">{b.payment.method} · {rupee(["Paid", "CASH_CONFIRMED"].includes(b.payment.status) ? b.payment.amount : (b.payment.received || 0))}{!["Paid", "CASH_CONFIRMED"].includes(b.payment.status) && b.payment.received ? " received so far" : ""}</p>
                            </div>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${b.payment.status === "Paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{b.payment.status}</span>
                        </div>
                        {b.invoice && (
                            <div className="mt-3 flex gap-2 border-t border-navy/10 pt-3">
                                <button data-testid="download-invoice-btn" onClick={downloadInvoice} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-navy py-2.5 text-sm font-bold text-white transition-transform active:scale-95"><Download size={16} /> Invoice</button>
                                <a data-testid="share-invoice-btn" href={whatsappLink(`My HOMIIE invoice ${b.invoice.invoice_number} for ${b.booking_id}`)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-full border border-navy/15 px-4 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-orange"><Share2 size={16} /> Share</a>
                            </div>
                        )}
                    </Card>
                )}

                {/* rating */}
                {["COMPLETED", "PAID", "CLOSED"].includes(b.status) && !b.rating && (
                    <Card>
                        <h3 className="font-heading font-bold text-navy">Rate this service</h3>
                        <div className="mt-3 flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map((s) => (
                                <button key={s} data-testid={`star-${s}`} onClick={() => setStars(s)}>
                                    <Star size={34} className={s <= stars ? "fill-orange text-orange" : "text-navy/20"} />
                                </button>
                            ))}
                        </div>
                        <button data-testid="submit-rating-btn" disabled={busy} onClick={submitRating} className="mt-4 w-full rounded-full bg-orange py-3 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-60">SUBMIT RATING</button>
                    </Card>
                )}
                {b.rating && (
                    <Card>
                        <div className="flex items-center justify-between">
                            <h3 className="font-heading font-bold text-navy">Your rating</h3>
                            <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={18} className={s <= b.rating.stars ? "fill-orange text-orange" : "text-navy/20"} />)}</div>
                        </div>
                    </Card>
                )}

                {/* CareBack */}
                {DONE.includes(b.status) && (
                    <div className="rounded-2xl border border-navy/10 bg-white p-4">
                        <div className="flex items-start gap-2.5">
                            <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-500" />
                            <div>
                                <h3 className="font-heading text-sm font-bold text-navy">CareBack Protection</h3>
                                <p className="text-xs text-navy/55">Service support if the same workmanship issue returns within the protection period.</p>
                            </div>
                        </div>
                        <button data-testid="report-problem-btn" onClick={() => setShowReport(true)} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full border border-navy/15 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-orange"><AlertCircle size={16} /> REPORT A PROBLEM</button>
                    </div>
                )}

                {/* status timeline */}
                <Card>
                    <h3 className="mb-4 font-heading font-bold text-navy">Tracking</h3>
                    <Timeline booking={b} isPaid={isPaid} />
                </Card>
                <Card>
                    <ActivityLog items={(b.status_history || []).map((h) => ({ ...h, label: h.note || h.status, note: h.note ? h.status : null }))} testid="booking-activity-log" title="Activity log (timestamped)" />
                </Card>
                <BookingChat bookingId={b.id} role="customer" />
                <CustomerCancel b={b} onDone={load} />
            </div>

            {/* Preferred pro prompt */}
            {showPref && (
                <Sheet onClose={() => savePref(false)}>
                    <div className="text-center">
                        <div className="mx-auto mb-3 w-fit rounded-full bg-orange/10 p-4"><HeartHandshake size={36} className="text-orange" /></div>
                        <h3 className="font-heading text-lg font-extrabold text-navy">Save {b.professional?.name} as your Preferred HOMIIE Pro?</h3>
                        <p className="mt-1 text-sm text-navy/55">Book them again easily from My HOMIIE Team.</p>
                        <div className="mt-5 flex gap-2">
                            <button data-testid="pref-no-btn" onClick={() => savePref(false)} className="flex-1 rounded-full border border-navy/15 py-3 text-sm font-bold text-navy">NOT NOW</button>
                            <button data-testid="pref-yes-btn" onClick={() => savePref(true)} className="flex-1 rounded-full bg-orange py-3 text-sm font-bold text-white">YES, SAVE PRO</button>
                        </div>
                    </div>
                </Sheet>
            )}

            {/* Report sheet */}
            {showReport && (
                <Sheet onClose={() => setShowReport(false)}>
                    <h3 className="font-heading text-lg font-extrabold text-navy">Report a problem</h3>
                    <p className="mt-1 text-sm text-navy/55">Tell us what went wrong. HOMIIE support will review your CareBack request.</p>
                    <textarea data-testid="report-text-input" value={reportText} onChange={(e) => setReportText(e.target.value)} rows={4} placeholder="The same issue came back..." className="mt-3 w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none focus:border-orange" />
                    <button data-testid="submit-report-btn" disabled={busy} onClick={submitReport} className="mt-3 w-full rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">SUBMIT REPORT</button>
                </Sheet>
            )}
            {/* UPI pay sheet */}
            {showPay && (
                <Sheet onClose={() => setShowPay(false)}>
                    <div className="text-center">
                        <h3 className="font-heading text-lg font-extrabold text-navy">{payMode === "visit" ? "Pay visit fee" : "Pay"} {rupee(payAmount)} via UPI</h3>
                        <p className="mt-1 text-sm text-navy/55">Scan the QR with any UPI app, or tap to open your UPI app.</p>
                        <div className="mx-auto mt-4 w-fit rounded-2xl border border-navy/10 bg-white p-4" data-testid="upi-qr">
                            {upiUri ? <QRCodeSVG value={upiUri} size={190} level="M" /> : <Loader2 className="h-6 w-6 animate-spin text-orange" />}
                        </div>
                        <p className="mt-2 text-xs text-navy/50">Paying <b className="text-navy">{payCfg?.upi_payee_name || "HOMIIE"}</b> · {payCfg?.upi_vpa}</p>
                        <a data-testid="open-upi-app-btn" href={upiUri} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-full bg-navy py-3 text-sm font-bold text-white active:scale-95"><Smartphone size={16} /> OPEN UPI APP</a>
                        <div className="mt-5 border-t border-navy/10 pt-4 text-left">
                            <label className="mb-1.5 block text-sm font-semibold text-navy">UPI reference / transaction ID (optional)</label>
                            <input data-testid="upi-ref-input" value={upiRef} onChange={(e) => setUpiRef(e.target.value)} placeholder="e.g. 4312XXXXXXX" className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none focus:border-orange" />
                            <button data-testid="confirm-paid-btn" disabled={busy} onClick={() => payUpi("UPI")} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-emerald-500 py-3 text-sm font-bold text-white active:scale-95 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}<Check size={16} /> I'VE PAID BY UPI</button>
                            <button data-testid="pay-cash-btn" disabled={busy} onClick={() => payUpi("CASH")} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-full border border-navy/15 py-3 text-sm font-bold text-navy active:scale-95 disabled:opacity-60">Pay by cash instead</button>
                            <p className="mt-2 text-center text-[11px] text-navy/40">Payment is confirmed by HOMIIE before your {payMode === "visit" ? "professional is assigned" : "invoice is generated"} — this protects you from any mistaken charge.</p>
                        </div>
                    </div>
                </Sheet>
            )}
            <div className="px-5 pb-8 pt-2"><SafetyReport bookingId={id} /></div>
        </CustomerShell>
    );
}

function Card({ children }) {
    return <div className="rounded-2xl border border-navy/10 bg-white p-4 shadow-[0_4px_16px_rgba(16,42,67,0.04)]">{children}</div>;
}

const TRACK_STEPS = [
    { label: "Booking Confirmed", st: ["REQUEST RECEIVED", "VISIT FEE PAID"] },
    { label: "Professional Assigned", st: ["PROFESSIONAL ASSIGNED"] },
    { label: "Professional Accepted", st: ["CONFIRMED"] },
    { label: "On the Way", st: ["ON THE WAY"] },
    { label: "Arrived", st: ["ARRIVED"] },
    { label: "Inspection", st: ["INSPECTION", "ESTIMATE APPROVAL REQUIRED"] },
    { label: "Work in Progress", st: ["WORK IN PROGRESS"] },
    { label: "Customer Review", st: ["CUSTOMER REVIEW", "REVISION REQUIRED"] },
    { label: "Work Approved", st: ["WORK DONE - PAYMENT PENDING"] },
    { label: "Payment Confirmed", st: ["PAID"], paid: true },
    { label: "Completed", st: ["COMPLETED", "CLOSED"] },
];

function Timeline({ booking, isPaid }) {
    const history = booking.status_history || [];
    const present = new Set(history.map((h) => h.status));
    present.add(booking.status);
    if (isPaid) present.add("PAID");
    const atMap = {};
    history.forEach((h) => { atMap[h.status] = h.at; });

    const steps = (booking.visit_fee > 0
        ? [{ label: "Visit Fee Paid", st: ["VISIT FEE PAID", "VISIT FEE PAYMENT PENDING"] }, ...TRACK_STEPS]
        : TRACK_STEPS);

    if (booking.status === "CANCELLED") {
        return (
            <div data-testid="tracking-cancelled" className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3.5">
                <X size={18} className="text-red-600" />
                <div><p className="text-sm font-bold text-red-700">Booking cancelled</p><p className="text-xs text-red-600/80">This booking is no longer active.</p></div>
            </div>
        );
    }

    const reached = steps.map((s) => s.st.some((x) => present.has(x)) || (s.paid && isPaid));
    let activeIdx = -1;
    reached.forEach((r, i) => { if (r) activeIdx = i; });

    return (
        <ol data-testid="tracking-timeline" className="relative">
            {steps.map((step, i) => {
                const done = i < activeIdx;
                const active = i === activeIdx;
                const at = step.st.map((x) => atMap[x]).find(Boolean);
                const last = i === steps.length - 1;
                return (
                    <li key={step.label} className="flex gap-3">
                        <div className="flex flex-col items-center">
                            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${done ? "bg-teal" : active ? "bg-navy ring-4 ring-navy/15" : "border border-border bg-[#F3F5F5]"}`}>
                                {done ? <Check size={13} className="text-white" strokeWidth={3} /> : active ? <span className="h-2 w-2 rounded-full bg-white" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted" />}
                            </span>
                            {!last && <span className={`w-0.5 flex-1 ${i < activeIdx ? "bg-teal" : "bg-border"}`} style={{ minHeight: 22 }} />}
                        </div>
                        <div className={`pb-5 ${last ? "" : ""}`}>
                            <p className={`text-sm font-bold ${active ? "text-navy" : done ? "text-navy/80" : "text-muted"}`}>{step.label}</p>
                            {at && <p className="text-[11px] text-navy/35">{new Date(at).toLocaleString("en-IN")}</p>}
                            {active && !at && <p className="text-[11px] font-medium text-teal">In progress…</p>}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
function Row({ label, value }) {
    return <div className="flex justify-between gap-4"><span className="shrink-0 text-navy/50">{label}</span><span className="text-right font-medium text-navy">{value || "—"}</span></div>;
}
function Sheet({ children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy/60 backdrop-blur-sm" onClick={onClose}>
            <div className="mx-auto w-full max-w-md animate-fade-up rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
}
